import React, { useState, useEffect, useRef } from 'react';
import { Building, TrendingUp, Leaf, Globe, Calendar, Users, DollarSign, FileText, ChevronRight, Phone, Mail, Download } from 'lucide-react';
import EnhancedInvestorSurvey from './EnhancedInvestorSurvey';
import ScheduleConsultation from './ScheduleConsultation';

const HomePage = ({ content, language, setCurrentPage }) => {
  const [heroVisible, setHeroVisible] = useState(false);
  const [servicesVisible, setServicesVisible] = useState(false);
  const [investorVisible, setInvestorVisible] = useState(false);
  const [showInvestorSurvey, setShowInvestorSurvey] = useState(false);
  const [showScheduling, setShowScheduling] = useState(false);

  const heroRef = useRef(null);
  const servicesRef = useRef(null);
  const investorRef = useRef(null);

  useEffect(() => {
    setHeroVisible(true);
    
    const isInViewport = (element, offset = 100) => {
      if (!element) return false;
      const rect = element.getBoundingClientRect();
      return rect.top <= (window.innerHeight - offset) && rect.bottom >= offset;
    };
    
    const handleScroll = () => {
      if (servicesRef.current && isInViewport(servicesRef.current)) {
        setServicesVisible(true);
      }
      if (investorRef.current && isInViewport(investorRef.current)) {
        setInvestorVisible(true);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDownloadInvestorDeck = () => {
    // This would typically trigger a download of a PDF or similar file
    // For now, we'll just show an alert - replace with actual download logic
    alert('Investor deck download would be triggered here');
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Updated Hero Section */}
      <div 
        ref={heroRef}
        className="h-screen flex flex-col justify-center items-center text-center px-6 relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black/80 z-10"></div>
        <div className="absolute inset-0 bg-gray-900 opacity-30"></div>
        
        <div className="relative z-20">
          <h1 className={`text-5xl md:text-7xl font-bold mb-6 transition-all duration-700 ${heroVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}>
            {language === 'en' 
              ? 'Building Infinite Cities' 
              : 'Construyendo Ciudades Infinitas'}
          </h1>
          <div className={`h-1 w-32 bg-gradient-to-r from-blue-500 to-green-500 mx-auto mb-8 transition-all duration-700 delay-200 ${heroVisible ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'}`}></div>
          <p className={`text-2xl md:text-3xl font-medium text-white mb-4 transition-all duration-700 delay-300 ${heroVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}>
            {language === 'en'
              ? 'Regenerative, Intelligent, Future-Resilient'
              : 'Regenerativas, Inteligentes, Resilientes al Futuro'}
          </p>
          <p className={`text-lg md:text-xl text-gray-300 max-w-4xl mb-12 leading-relaxed transition-all duration-700 delay-400 ${heroVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}>
            {language === 'en'
              ? 'We\'re creating cities and infrastructure that evolve across generations — integrating sustainability, AI, and cross-border capital into every stage of development.'
              : 'Estamos creando ciudades e infraestructura que evolucionan a través de generaciones — integrando sostenibilidad, IA y capital transfronterizo en cada etapa del desarrollo.'}
          </p>
          <div className={`flex flex-col sm:flex-row gap-4 justify-center transition-all duration-700 delay-500 ${heroVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}>
            <button 
              onClick={() => setCurrentPage && setCurrentPage('projects')}
              className="px-8 py-4 bg-white text-black font-medium rounded hover:bg-gray-200 transition-colors"
            >
              {language === 'en' ? 'View Projects' : 'Ver Proyectos'}
            </button>
            <button 
              onClick={handleDownloadInvestorDeck}
              className="px-8 py-4 bg-gradient-to-r from-blue-600 to-green-600 text-white font-medium rounded hover:from-blue-700 hover:to-green-700 transition-all duration-300 flex items-center"
            >
              <Download size={18} className="mr-2" />
              {language === 'en' ? 'Download Investor Deck' : 'Descargar Deck de Inversionista'}
            </button>
            <button 
              onClick={() => setShowScheduling(true)}
              className="px-8 py-4 border border-white text-white font-medium rounded hover:bg-white hover:text-black transition-colors"
            >
              {language === 'en' ? 'Schedule a Call' : 'Agendar una Llamada'}
            </button>
          </div>
        </div>
      </div>

      {/* Enhanced Investor Relations Section */}
      <div 
        ref={investorRef}
        className="py-24 px-6 md:px-12 bg-gradient-to-br from-gray-900 via-black to-gray-900 relative overflow-hidden"
      >
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, white 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}></div>
        </div>
        
        <div className="max-w-6xl mx-auto relative z-10">
          <div className={`text-center mb-16 transition-all duration-700 ${investorVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}>
            <h2 className="text-5xl font-bold mb-6 bg-gradient-to-r from-white via-gray-200 to-white bg-clip-text text-transparent">
              {language === 'en' ? 'Invest with Us' : 'Invierte con Nosotros'}
            </h2>
            <div className="h-1 w-32 bg-gradient-to-r from-blue-500 to-green-500 mx-auto mb-8"></div>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              {language === 'en'
                ? 'Join a select group of investors building the future of real estate across the Americas. Discover exclusive opportunities in high-growth markets with sustainable returns.'
                : 'Únete a un grupo selecto de inversionistas construyendo el futuro de los bienes raíces en las Américas. Descubre oportunidades exclusivas en mercados de alto crecimiento con retornos sostenibles.'}
            </p>
          </div>

          {/* Investment Highlights */}
          <div className={`grid md:grid-cols-3 gap-8 mb-16 transition-all duration-700 delay-200 ${investorVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}>
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 p-8 rounded-2xl border border-gray-700 hover:border-gray-600 transition-all duration-300 hover:transform hover:scale-105">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl mb-6 flex items-center justify-center">
                <TrendingUp size={28} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4">
                {language === 'en' ? 'Proven Returns' : 'Retornos Comprobados'}
              </h3>
              <p className="text-gray-400 leading-relaxed">
                {language === 'en'
                  ? 'Track record of delivering 15-25% IRR across diversified real estate portfolios in emerging markets.'
                  : 'Historial de entregar 15-25% TIR en portafolios inmobiliarios diversificados en mercados emergentes.'}
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-800 to-gray-900 p-8 rounded-2xl border border-gray-700 hover:border-gray-600 transition-all duration-300 hover:transform hover:scale-105">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl mb-6 flex items-center justify-center">
                <Globe size={28} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4">
                {language === 'en' ? 'Cross-Border Expertise' : 'Experiencia Transfronteriza'}
              </h3>
              <p className="text-gray-400 leading-relaxed">
                {language === 'en'
                  ? 'Deep understanding of US and Latin American markets, regulations, and investment opportunities.'
                  : 'Profundo entendimiento de mercados, regulaciones y oportunidades de inversión en EE.UU. y Latinoamérica.'}
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-800 to-gray-900 p-8 rounded-2xl border border-gray-700 hover:border-gray-600 transition-all duration-300 hover:transform hover:scale-105">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl mb-6 flex items-center justify-center">
                <Users size={28} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4">
                {language === 'en' ? 'Exclusive Access' : 'Acceso Exclusivo'}
              </h3>
              <p className="text-gray-400 leading-relaxed">
                {language === 'en'
                  ? 'Limited partnership opportunities with institutional-grade due diligence and transparent reporting.'
                  : 'Oportunidades limitadas de sociedad con due diligence de grado institucional y reportes transparentes.'}
              </p>
            </div>
          </div>

          {/* Enhanced Call-to-Action Buttons */}
          <div className={`flex flex-col md:flex-row gap-6 justify-center items-center transition-all duration-700 delay-400 ${investorVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}>
            <button 
              onClick={() => setShowInvestorSurvey(true)}
              className="group px-10 py-4 bg-gradient-to-r from-blue-600 to-green-600 text-white font-bold rounded-xl hover:from-blue-700 hover:to-green-700 transition-all duration-300 flex items-center transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              <FileText size={20} className="mr-3" />
              {language === 'en' ? 'Complete Investor Survey' : 'Completar Encuesta de Inversionista'}
              <ChevronRight size={20} className="ml-3 group-hover:translate-x-1 transition-transform" />
            </button>

            <button 
              onClick={() => setShowScheduling(true)}
              className="group px-10 py-4 border-2 border-white text-white font-bold rounded-xl hover:bg-white hover:text-black transition-all duration-300 flex items-center transform hover:scale-105"
            >
              <Calendar size={20} className="mr-3" />
              {language === 'en' ? 'Schedule Consultation' : 'Agendar Consulta'}
              <ChevronRight size={20} className="ml-3 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          {/* Quick Contact */}
          <div className={`mt-16 text-center transition-all duration-700 delay-600 ${investorVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}>
            <p className="text-gray-400 mb-4">
              {language === 'en' ? 'Qualified investors can also reach us directly:' : 'Inversionistas calificados también pueden contactarnos directamente:'}
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <a href="mailto:investors@infinitydevelopment.com" className="flex items-center text-gray-300 hover:text-white transition-colors">
                <Mail size={18} className="mr-2" />
                investors@infinitydevelopment.com
              </a>
              <a href="tel:+1-555-INVEST" className="flex items-center text-gray-300 hover:text-white transition-colors">
                <Phone size={18} className="mr-2" />
                +1 (555) INVEST
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Services Section */}
      <div 
        ref={servicesRef}
        className="py-24 px-6 md:px-12 bg-gray-900"
      >
        <div className="max-w-6xl mx-auto">
          <h2 className={`text-4xl font-bold mb-4 text-center transition-all duration-700 ${servicesVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}>
            {content?.[language]?.home?.services || 'Our Services'}
          </h2>
          <div className={`h-1 w-24 bg-white mx-auto mb-16 transition-all duration-700 delay-200 ${servicesVisible ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'}`}></div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {(content?.[language]?.home?.servicesList || [
              { title: 'Development', description: 'Innovative real estate development projects' },
              { title: 'Investment', description: 'Strategic investment opportunities' },
              { title: 'Sustainability', description: 'Eco-friendly development practices' },
              { title: 'Global Reach', description: 'International market expertise' }
            ]).map((service, index) => (
              <div 
                key={index} 
                className={`bg-black p-8 rounded-lg hover:bg-gray-800 transition-all duration-700 ${servicesVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${300 + index * 100}ms` }}
              >
                <div className="w-16 h-16 bg-white bg-opacity-10 rounded-full mb-6 flex items-center justify-center">
                  {index % 4 === 0 && <Building size={24} className="text-white" />}
                  {index % 4 === 1 && <TrendingUp size={24} className="text-white" />}
                  {index % 4 === 2 && <Leaf size={24} className="text-white" />}
                  {index % 4 === 3 && <Globe size={24} className="text-white" />}
                </div>
                <h3 className="text-xl font-medium mb-4">{service.title}</h3>
                <p className="text-gray-400 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Company Positioning Statement */}
      <div className="py-24 px-6 md:px-12 bg-black">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-3xl md:text-4xl font-bold mb-8 text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.3)]">
            {language === 'en' 
              ? 'Building Tomorrow\'s Communities Today'
              : 'Construyendo las Comunidades del Mañana Hoy'}
          </h3>
          <p className="text-xl text-gray-300 leading-relaxed">
            {language === 'en'
              ? 'We are reshaping urban landscapes across the Americas through innovative development, sustainable design, and strategic cross-border partnerships that create lasting value for communities and investors alike.'
              : 'Estamos transformando los paisajes urbanos de las Américas a través de desarrollo innovador, diseño sostenible y alianzas estratégicas transfronterizas que crean valor duradero para comunidades e inversionistas por igual.'}
          </p>
        </div>
      </div>

      {/* Enhanced Investor Survey Modal */}
      <EnhancedInvestorSurvey 
        showSurvey={showInvestorSurvey} 
        setShowSurvey={setShowInvestorSurvey}
        language={language}
      />

      {/* Enhanced Schedule Consultation Modal */}
      <ScheduleConsultation 
        showScheduling={showScheduling} 
        setShowScheduling={setShowScheduling}
        language={language}
      />
    </div>
  );
};

export default HomePage;